import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Users, Calendar, Truck, Phone, Mail, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

// Pizza box for catering
const PIZZA_BOX_IMG = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/63gnwsdb_image.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dining-guide-3/artifacts/egfzykn3_image.png";

const CateringSection = () => {
  const { t, language } = useLanguage();

  const features = [
    { icon: Calendar, text: t('catering.features')[0], color: 'amber' },
    { icon: Truck, text: t('catering.features')[1], color: 'emerald' },
    { icon: Building2, text: t('catering.features')[2], color: 'blue' },
    { icon: Users, text: t('catering.features')[3], color: 'pink' },
  ];

  const colorClasses = {
    amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-400/20' },
    emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-400/20' },
    blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-400/20' },
    pink: { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-400/20' },
  };

  return (
    <section 
      id="catering" 
      className="relative overflow-hidden"
      data-testid="catering-section"
    >
      {/* Premium Section Header with Background Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${PIZZA_BOX_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/60 via-hanse-dark/70 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          {/* Sparkle Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-amber-500/20 to-amber-600/20 backdrop-blur-sm px-4 py-2 rounded-full border border-amber-400/30"
          >
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="text-amber-300 font-semibold text-sm uppercase tracking-wider">
              {language === 'de' ? 'Für Events & Firmen' : 'For Events & Companies'}
            </span>
            <Sparkles className="w-5 h-5 text-amber-400" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {t('catering.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-base md:text-lg max-w-2xl"
          >
            {t('catering.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Premium Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Left: Features Grid */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {features.map((feature, index) => {
                  const colors = colorClasses[feature.color];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`bg-white/5 backdrop-blur-sm rounded-xl p-4 md:p-5 border border-white/10 hover:${colors.border} transition-all duration-300 group`}
                    >
                      <div className={`w-10 h-10 md:w-12 md:h-12 ${colors.bg} rounded-full flex items-center justify-center mb-3`}>
                        <feature.icon className={`w-5 h-5 md:w-6 md:h-6 ${colors.text}`} />
                      </div>
                      <p className="text-white/80 font-medium text-sm md:text-base">{feature.text}</p>
                    </motion.div>
                  );
                })}
              </div>

              {/* Reference Quote */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 backdrop-blur-sm rounded-xl p-5 md:p-6 border-l-4 border-amber-400"
              >
                <p className="text-white/70 text-sm md:text-base italic">
                  "{t('catering.reference')}"
                </p>
                <p className="text-amber-400 text-sm mt-2 font-medium">
                  — Corporate Client
                </p>
              </motion.div>
            </motion.div>

            {/* Right: CTA Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10"
            >
              {/* Logo Badge */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-hanse-red to-amber-500 rounded-full blur-lg opacity-30"></div>
                  <img
                    src={LOGO_URL}
                    alt="Hanse Pizza Logo"
                    className="relative w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-amber-400/50"
                  />
                </div>
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold">
                    <span className="text-hanse-red">HANSE</span>{' '}
                    <span className="text-white">PIZZA</span>
                  </h3>
                  <p className="text-amber-400 text-sm">B2B Catering Service</p>
                </div>
              </div>

              {/* Info */}
              <p className="text-white/70 text-sm md:text-base mb-6">
                {language === 'de' 
                  ? 'Kontaktieren Sie uns für individuelle Catering-Angebote. Wir liefern frisch und pünktlich zu Ihrem Event.'
                  : 'Contact us for customized catering offers. We deliver fresh and on time to your event.'
                }
              </p>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={() => window.location.href = 'tel:+4942148535145'}
                  className="w-full bg-hanse-red hover:bg-hanse-red/90 text-white px-6 py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-base"
                  data-testid="catering-cta-btn"
                >
                  <Phone className="w-5 h-5" />
                  {t('catering.cta')}
                </Button>
                <Button
                  onClick={() => window.location.href = 'mailto:Info@hansepizzabremen.de?subject=Catering%20Anfrage'}
                  variant="outline"
                  className="w-full border-2 border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-6 py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-sm md:text-base"
                >
                  <Mail className="w-5 h-5" />
                  Info@hansepizzabremen.de
                </Button>
              </div>

              {/* B2B Badge */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, type: "spring" }}
                className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 to-amber-600/10 px-4 py-2 rounded-full border border-amber-400/30"
              >
                <Building2 className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300 font-semibold text-sm">
                  {language === 'de' ? 'B2B & Events willkommen' : 'B2B & Events welcome'}
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CateringSection;
